import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KIND_LABEL = {
  record_approval: "Record approval",
  config_publication: "Configuration publishing",
  access_certification: "Access certification",
  contract_renewal: "Contract renewal",
  sourcing_decision: "Vendor selection",
};

export default function Approvals() {
  const [inbox, setInbox] = useState(null);
  const [reasons, setReasons] = useState({});
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    apiFetch("/approvals/inbox").then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setError("Your approvals inbox is not available"); return; }
      setError(null);
      setInbox(body);
    });
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    load();
  }, [navigate, load]);

  const decide = (item, decision) => {
    const reason = (reasons[item.id] || "").trim();
    if (!reason) { setMessage("A reason is required before you decide"); return; }
    apiFetch("/approvals/inbox/decide", {
      method: "POST",
      body: JSON.stringify({ kind: item.kind, id: item.id, decision, reason }),
    }).then(async (r) => {
      const out = await r.json();
      if (!r.ok) {
        setMessage(`Decision failed — ${typeof out.detail === "string" ? out.detail
                                                                      : JSON.stringify(out.detail)}`);
        return;
      }
      setMessage(`${item.what} — ${out.outcome}`);
      setReasons((s) => ({ ...s, [item.id]: "" }));
      load();
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="approvals-title">Approvals</h1>
          <p className="text-sm text-muted-foreground">
            Everything waiting on your decision, in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="approvals-changes-link">
            <Link to="/changes">Changes</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="approvals-error">{error}</p>}
        {message && (
          <p className="text-sm text-amber-800" data-testid="approvals-message">{message}</p>
        )}

        {inbox && (
          <>
            <section className="grid gap-3 md:grid-cols-3 text-sm" data-testid="approvals-totals">
              {[["Waiting on you", inbox.totals.waiting, "waiting"],
                ["You can decide here", inbox.totals.decidable_here, "decidable"],
                ["Decided on their own record", inbox.totals.elsewhere, "elsewhere"]]
                .map(([label, value, id]) => (
                  <div key={id} className="border rounded-md px-3 py-2"
                       data-testid={`approvals-total-${id}`}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-2xl font-semibold">{value}</p>
                  </div>
                ))}
            </section>

            <p className="text-sm text-muted-foreground" data-testid="approvals-method">
              {inbox.method}
            </p>
            {inbox.delegating_for.length > 0 && (
              <p className="text-sm" data-testid="approvals-delegations">
                You are also deciding on behalf of {inbox.delegating_for.length} colleague(s) who
                delegated to you.
              </p>
            )}

            <section className="space-y-3" data-testid="approvals-list">
              <h2 className="text-base md:text-lg font-medium">Waiting on you</h2>
              {inbox.items.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="approvals-empty">
                  Nothing is waiting on your decision.
                </p>
              )}
              {inbox.items.map((item) => (
                <div key={`${item.kind}-${item.id}`}
                     className="border rounded-md p-4 space-y-2"
                     data-testid={`approval-item-${item.id}`}>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground"
                          data-testid={`approval-kind-${item.id}`}>
                      {KIND_LABEL[item.kind] || item.kind}
                    </span>
                    {item.reference && <span className="font-mono text-sm">{item.reference}</span>}
                    <span className="text-sm font-medium">{item.what}</span>
                  </div>
                  <p className="text-sm text-muted-foreground" data-testid={`approval-why-${item.id}`}>
                    {item.why}
                    {item.requested_by ? ` · raised by ${item.requested_by}` : ""}
                    {item.due_at ? ` · due ${item.due_at.slice(0, 10)}` : ""}
                    {item.delegate_for ? " · as a delegate" : ""}
                  </p>
                  {item.decidable ? (
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="space-y-1 flex-1 min-w-[12rem]">
                        <label htmlFor={`approval-reason-${item.id}`} className="text-xs font-medium">
                          Reason (required)
                        </label>
                        <Input id={`approval-reason-${item.id}`}
                               data-testid={`approval-reason-${item.id}`}
                               value={reasons[item.id] || ""}
                               onChange={(e) => setReasons({ ...reasons, [item.id]: e.target.value })} />
                      </div>
                      <Button size="sm" data-testid={`approval-approve-${item.id}`}
                              onClick={() => decide(item, "approve")}>
                        {item.approve_label}
                      </Button>
                      <Button size="sm" variant="outline"
                              data-testid={`approval-reject-${item.id}`}
                              onClick={() => decide(item, "reject")}>
                        {item.reject_label}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm" data-testid={`approval-elsewhere-${item.id}`}>
                      This one is recorded on its own record.{" "}
                      {item.link && (
                        <Link to={item.link} className="underline"
                              data-testid={`approval-link-${item.id}`}>
                          Open it
                        </Link>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
